/* global Sys, Services, Language, Platform */
Sys.ns("Services");

/**
 * The autoplay stop condition.
 *
 * @typedef {Object} AutoplayStopCondition
 * @property {string} id - The stop identifier.
 * @property {boolean|*} defaultValue - The default value (boolean indicated activation or the attribute value).
 * @property {boolean} [enabled=true] - Whether or not the condition is enabled.
 * @property {boolean} [active=false] - Whether or not the condition is activated.
 * @property {Object} [attributes] - The attributes describing the detailed stop condition settings.
 * @property {string} attributes.label - The label describing the value field.
 * @property {*} [attributes.value=null] - The initial value.
 * @property {boolean} [attributes.required=false] - Whether or not the condition is required for autoplay to be available.
 * @property {Function} [attributes.validate] - The function uses for validating the value when the setting is required.
 */

/**
 * The Autoplay Service responsible for managing the autoplay round selection and stop conditions.
 *
 * @class Services.Autoplay
 * @extends Services.Base
 */
Services.Autoplay = {
    DEFAULT_ROUNDS: [10, 25, 50, 100, 250, 500, 750, 1000],
    MIN_ROUNDS: 5,
    MAX_ROUNDS: 1000,
    MAX_ROUND_OPTIONS: 8,
    SETTINGS_DISABLED_FREE_ROUNDS: [
        "stopAutoplayIfWinExceeds",
        "stopAutoplayIfBalanceIncreasedBy",
        "stopAutoplayIfBalanceDecreasedBy"
    ],

    constructor: function(config) {
        Services.Autoplay.superclass.constructor.apply(this, arguments);

        this._active = false;
        this._availableRounds = this._getAvailableRounds(
            config.initResponse,
            this.DEFAULT_ROUNDS
        );
        this._available = this._availableRounds !== null;
        this._rounds = 0;
        this._stopConditions = this._getDefaultStopConditions(config.initResponse, config.ISO);
        this._settingsOpen = false;
        this._initResponse = config.initResponse;
    },

    _setupEvents: function() {
        this.observable.on({
            "notify:autoPlayer.enabled": this._enabled.bind(this),
            "notify:autoPlayer.starting": this._started.bind(this),
            "notify:autoPlayer.startRound": this._roundStarted.bind(this),
            "notify:autoPlayer.reduceCounter": this._counterReduced.bind(this),
            "notify:autoPlayer.stopped": this._stopped.bind(this),
            "notify:freeRounds.started": this._freeRoundsStarted.bind(this),
            "notify:freeRounds.ended": this._freeRoundsEnded.bind(this)
        });
    },

    /**
     * Checks if autoplay is ongoing.
     *
     * @returns {boolean} Whether or not autoplay is ongoing.
     */
    isActive: function() {
        return this._active;
    },

    /**
     * Checks if autoplay is available (can be disabled through SPP settings).
     *
     * @returns {boolean} Whether or not autoplay is available.
     */
    isAvailable: function() {
        return this._available;
    },

    /**
     * Gets the autoplay options available to the player.
     *
     * @returns {number} The autoplay options available to the player or null if autoplay is not available.
     */
    getRoundsAvailable: function() {
        return this._availableRounds;
    },

    /**
     * Gets the number of rounds remaining.
     *
     * @returns {number} The rounds remaining or 0 if autoplay is not active.
     */
    getRemaining: function() {
        return this._rounds;
    },

    /**
     * Sets the number of autoplay rounds to play.
     *
     * Must be one of the available options retrievable from getRoundsAvailable.
     *
     * @param {number} rounds The number of rounds to play.
     * @returns {void}
     */
    setRounds: function(rounds) {
        if (
            !(
                this.isAvailable() &&
                this._validateRound(rounds) &&
                this.getRoundsAvailable().indexOf(rounds) > -1
            )
        ) {
            return;
        }

        this._rounds = rounds;

        this.publish("roundsChanged", this._rounds);

        Services.settingsManager.storeSetting("autoPlayNrSpins", rounds);
    },

    /**
     * Starts autoplay if a number of rounds have been selected an autoplay is not ongoing.
     *
     * @returns {void}
     */
    start: function() {
        if (this.canStartAutoplay()) {
            if (this.getRemaining() > 0 && !this.isActive()) {
                this.observable.fireEvent("request:autoPlayer.start");
            }
        }
        else {
            this.showWarningDialog();
        }
    },

    /**
     * Stops autoplay.
     *
     * @returns {void}
     */
    stop: function() {
        this._rounds = 0;

        Services.settingsManager.storeSetting("autoPlayNrSpins", 0);
    },

    /**
     * Tells if the autoplay is in restricted mode.
     *
     * @returns {boolean} If autoplay is in restricted mode.
     */
    isRestrictedMode: function() {
        return this._initResponse.autoplayLossLimitEnabled;
    },

    /**
     * Tells if the settings are valid to start autoplay round.
     *
     * @private
     * @returns {boolean} If settings are valid.
     */
    canStartAutoplay: function() {
        var isRestrictedMode = this._initResponse.autoplayLossLimitEnabled,
            ifCashDecreases = Services.settingsManager.getSetting("stopAutoplayIfBalanceDecreasedBy"),
            isFreeRounds = Services.freeRounds.isActive(),
            isBetOverlapping = Services.bet.isBetOverlapping();

        if (isFreeRounds) {
            return true;
        }
        if (isBetOverlapping || (isRestrictedMode && !ifCashDecreases)) {
            return false;
        }

        return true;
    },

    /**
     * Shows warning dialog if autoplay session is not allowed to start.
     *
     * @returns {void}
     */
    showWarningDialog: function() {
        var me = this,
            languageManager = Services.languageManager,
            ifCashDecreasesByWarningText = languageManager.getText(Language.Keys.autoplay_setting_ifCashDecreasesByWarning),
            betOverlappingText = languageManager.getText(Language.Keys.autoplay_setting_ifCashDecreasesByWarningTouch),
            isBetOverlapping = Services.bet.isBetOverlapping(),
            config;

        if (Platform.isDesktopDevice) {
            config = {
                title: languageManager.getText(Language.Keys.autoplay_setting_ifCashDecreasesBy),
                text: isBetOverlapping ? betOverlappingText : ifCashDecreasesByWarningText,
                buttons: [{ text: languageManager.getText(Language.Keys.btn_ok) }]
            };
        }
        else {
            config = {
                title: languageManager.getText(Language.Keys.autoplay_setting_ifCashDecreasesBy),
                text: betOverlappingText,
                buttons: [{
                    text: languageManager.getText(Language.Keys.btn_close),
                    action: Services.autoplay.stop.bind(me)
                },
                {
                    text: languageManager.getText(Language.Keys.btn_ok),
                    action: function() {
                        me.observable.fireEvent("request:spinSettings.openAdvancedSettingKeyboard", "stopAutoplayIfBalanceDecreasedBy");
                    }
                }]
            };
        }

        Services.dialog.show(config);
    },

    /**
     * Adds the stop condition.
     *
     * @param {AutoplayStopCondition} condition The stop condition.
     * @returns {boolean} Whether or not the stop condition was added.
     */
    addStopCondition: function(condition) {
        var attributes = condition.attributes,
            id = condition.id,
            value = condition.active || false;

        this._validateStopCondition(condition);

        if (this.hasStopCondition(condition.id)) {
            return false;
        }

        this._stopConditions.push(condition);

        if (attributes) {
            value =
                typeof attributes.value === "undefined" ? null : attributes.value;
        }

        Services.settingsManager.storeSetting(id, value);

        this.publish("stopConditionAdded", id);

        return true;
    },

    /**
     * Checks if the stop condition exists.
     *
     * @param {string} id The id of the stop condition.
     * @returns {boolean} Whether or not the stop condition exists.
     */
    hasStopCondition: function(id) {
        return this.getStopCondition(id) !== null;
    },

    /**
     * Gets the stop conditions.
     *
     * @returns {Object[]} The stop conditions.
     */
    getStopConditions: function() {
        return this._stopConditions.map(function(setting) {
            return {
                id: setting.id,
                text: setting.text,
                active: setting.active,
                enabled: setting.enabled,
                attributes: setting.attributes
            };
        });
    },

    /**
     * Updates the stop condition.
     *
     * @param {AutoplayStopCondition} updatedCondition The stop condition.
     * @returns {void}
     */
    updateStopCondition: function(updatedCondition) {
        var id = updatedCondition.id,
            condition = this.getStopCondition(id),
            value = updatedCondition.active,
            attributes;

        if (!condition) {
            throw new Error("Condition with id '" + id + "' was not found");
        }

        condition.active = updatedCondition.active;
        condition.enabled = updatedCondition.enabled;
        attributes = updatedCondition.attributes;

        if (updatedCondition.active === false) {
            value = null;
            if (condition.attributes) {
                condition.attributes.value = null;
            }
        }

        if (attributes) {
            value = updatedCondition.attributes.value;

            if (attributes.required) {
                condition.active = true;
                if (typeof attributes.validate === "function" && !attributes.validate(value)) {
                    return;
                }
            }

            if (condition.attributes) {
                condition.attributes.value = value;
            }
        }

        Services.settingsManager.storeSetting(id, value);

        this.publish("stopConditionChanged", id, value);
    },

    /**
     * Resets the stop conditions to their default values.
     *
     * @returns {void}
     */
    resetStopConditions: function() {
        this._stopConditions.forEach(function(condition) {
            if (condition.attributes) {
                condition.active = Boolean(condition.attributes.required);
                condition.attributes.value = condition.defaultValue;
            }
            else {
                condition.active = condition.defaultValue;
            }

            this.publish(
                "stopConditionChanged",
                condition.id,
                condition.defaultValue
            );

            Services.settingsManager.storeSetting(
                condition.id,
                condition.defaultValue
            );
        }.bind(this));
    },

    /**
     * Removes the stop condition.
     *
     * @param {string} id The stop condition id.
     * @returns {boolean} Whether or not the stop condition was removed.
     */
    removeStopCondition: function(id) {
        var condition = this.getStopCondition(id);

        if (!condition) {
            return false;
        }

        this._stopConditions.splice(
            this._stopConditions.indexOf(condition),
            1
        );

        Services.settingsManager.storeSetting(id, null);

        this.publish("stopConditionRemoved", id);

        return true;
    },

    /**
     * Changes state of AutoPlay settings.
     *
     * @param {string} stateName – Name of state, 'opened' or 'closed'.
     * @returns {void}
     */
    setSettingsState: function(stateName) {
        switch (stateName) {
            case "opened":
                this.openSettings();
                break;

            case "closed":
                this.closeSettings();
                break;

            default:
                throw new Error("Unsupported AutoPlay state: " + stateName);
        }
    },

    /**
     * Checks if AutoPlay settings are being shown.
     *
     * @returns {boolean} Whether or not the AutoPlay settings are shown.
     */
    isShowingSettings: function() {
        return this._settingsOpen;
    },

    /**
     * Opens the AutoPlay settings (if not already open).
     *
     * @returns {void}
     */
    openSettings: function() {
        if (this._settingsOpen) {
            return;
        }

        this._settingsOpen = true;

        this.publish("opened");
    },

    /**
     * Closes the AutoPlay settings (if not already closed).
     *
     * @returns {void}
     */
    closeSettings: function() {
        if (!this._settingsOpen) {
            return;
        }

        this._settingsOpen = false;

        this.publish("closed");
    },

    /**
     * Gets the default stop conditions.
     *
     * @param {Object} response The init server response.
     * @param {string} ISO The ISO currency code (eg. EUR, SEK GBP).
     * @returns {AutoplayStopCondition[]} THe stop conditions.
     */
    _getDefaultStopConditions: function(response, ISO) {
        var me = this,
            conditions = [],
            lossLimit = {
                id: "stopAutoplayIfBalanceDecreasedBy",
                text: Services.languageManager.getText(Language.Keys.autoplay_setting_ifCashDecreasesBy),
                active: false,
                enabled: true,
                defaultValue: null,
                attributes: {
                    label: ISO,
                    value: null
                }
            };

        conditions.push({
            id: "stopAutoplayOnAnyWin",
            text: Services.languageManager.getText(Language.Keys.autoplay_setting_onAnyWin),
            enabled: true,
            active: false,
            defaultValue: false
        });

        conditions.push({
            id: "stopAutoplayIfWinExceeds",
            text: Services.languageManager.getText(Language.Keys.autoplay_setting_ifWinExeeds),
            enabled: true,
            active: false,
            defaultValue: null,
            attributes: {
                label: ISO,
                value: null
            }
        });

        conditions.push({
            id: "stopAutoplayIfBalanceIncreasedBy",
            text: Services.languageManager.getText(Language.Keys.autoplay_setting_ifCashIncreasesBy),
            active: false,
            enabled: true,
            defaultValue: null,
            attributes: {
                label: ISO,
                value: null
            }
        });

        if (response.autoplayLossLimitEnabled) {
            lossLimit.attributes.required = true;
            lossLimit.active = true;
            lossLimit.attributes.validate = function(value) {
                return value > 0;
            };
        }

        conditions.push(lossLimit);

        if (response.autoplayLossLimitEnabled && response.freeRoundsLeft) {
            conditions.forEach(function(setting) {
                if (Sys.contains(me.SETTINGS_DISABLED_FREE_ROUNDS, setting.id)) {
                    setting.enabled = false;
                    setting.active = false;
                }
            });
        }

        return conditions;
    },

    /**
     * Gets the stop condition associated with the ID.
     *
     * @param {string} id The id of the stop condition.
     * @returns {AutoplayStopCondition|null} The stop condition or null if it was not found.
     */
    getStopCondition: function(id) {
        return (
            this._stopConditions.filter(function(condition) {
                return condition.id === id;
            })[0] || null
        );
    },

    /**
     * Handles when the auto player is enabled.
     *
     * @private
     * @param {number} rounds The autoplay rounds.
     * @returns {void}
     */
    _enabled: function(rounds) {
        this._rounds = rounds;
    },

    /**
     * Handles when the auto player is started.
     *
     * @private
     * @returns {void}
     */
    _started: function() {
        this._active = true;

        this.publish("started");
    },

    /**
     * Handles when the auto player starts a new round.
     *
     * @private
     * @param {number} roundsLeft The remaining autoplay rounds.
     * @returns {void}
     */
    _roundStarted: function(roundsLeft) {
        // do nothing
    },

    /**
     * Handles when the counter reduces.
     *
     * @private
     * @param {number} roundsLeft The remaining autoplay rounds.
     * @returns {void}
     */
    _counterReduced: function(roundsLeft) {
        this._rounds = roundsLeft;

        this.publish("roundStarted", roundsLeft);
    },

    /**
     * Handles when the auto player is stopped.
     *
     * @private
     * @returns {void}
     */
    _stopped: function() {
        this._rounds = 0;

        this._active = false;

        this.publish("stopped");
    },

    /**
     * Gets the available autoplay rounds.
     *
     * @private
     * @param {Object} response The server response.
     * @param {number[]} defaultRounds The default options to fall back to.
     * @returns {number[]|null} The available round options or null if autoplay is disabled.
     */
    _getAvailableRounds: function(response, defaultRounds) {
        var rounds, countOfRoundsInMaxRange, allRoundsValid;

        // Autoplay is disabled.
        if (response.autoplay === "0") {
            return null;
        }

        // Fall back to default rounds if autoplay is not sent correctly.
        if (typeof response.autoplay !== "string" || response.autoplay === "") {
            return defaultRounds;
        }

        rounds = response.autoplay.split(",").map(function(value) {
            return parseInt(value, 10);
        });

        // Fall back to default rounds if there are more options than allowed or if they are invalid.
        // See: GREQ-26091 for more information
        countOfRoundsInMaxRange = rounds.length <= this.MAX_ROUND_OPTIONS;
        allRoundsValid = rounds.every(this._validateRound.bind(this));
        if (!countOfRoundsInMaxRange || !allRoundsValid) {
            return defaultRounds;
        }

        return rounds.sort(function(a, b) {
            return a - b;
        });
    },

    /**
     * Validates the rounds.
     *
     * @private
     * @param {number} round The number of rounds.
     * @returns {boolean} Whether or not the rounds are valid.
     */
    _validateRound: function(round) {
        return (
            !isNaN(round) &&
            round >= this.MIN_ROUNDS &&
            round <= this.MAX_ROUNDS
        );
    },

    /**
     * Validates the stop condition.
     *
     * @private
     * @param {AutoplayStopCondition} condition The stop condition to validate.
     * @throws If the stop condition is not valid.
     * @returns {void}
     */
    _validateStopCondition: function(condition) {
        var attributes = condition.attributes;

        if (typeof condition.id !== "string") {
            throw new Error(
                "The autoplay stop condition must have a string 'id'."
            );
        }

        if (typeof condition.text !== "string") {
            throw new Error(
                "The autoplay stop condition must have a title 'text'."
            );
        }

        if (typeof condition.defaultValue === "undefined") {
            throw new Error(
                "The autoplay stop condition must have a default value."
            );
        }

        if (attributes) {
            if (typeof attributes.label !== "string") {
                throw new Error(
                    "The autoplay stop condition must have a string 'label' for its attributes."
                );
            }

            if (
                Boolean(attributes.required) &&
                typeof attributes.validate !== "function"
            ) {
                throw new Error(
                    "The autoplay stop condition must have a 'validate' function in its attributes if it is marked as 'required'."
                );
            }
        }
    },

    _freeRoundsStarted: function() {
        var me = this;

        me._stopConditions.forEach(function(setting) {
            if (Sys.contains(me.SETTINGS_DISABLED_FREE_ROUNDS, setting.id)) {
                setting.enabled = false;
                setting.attributes.required = false;
                setting.active = false;
                me.updateStopCondition(setting);
            }
        });
    },

    _freeRoundsEnded: function() {
        var me = this;

        me._stopConditions.forEach(function(setting) {
            if (Sys.contains(me.SETTINGS_DISABLED_FREE_ROUNDS, setting.id)) {
                setting.enabled = true;
            }
            if (me._initResponse.autoplayLossLimitEnabled) {
                if (setting.id === "stopAutoplayIfBalanceDecreasedBy") {
                    setting.attributes.required = true;
                    setting.attributes.validate = null;
                }
            }
            me.updateStopCondition(setting);
        });
    }
};

Services.Autoplay = Sys.extend(
    Services.Base,
    Services.Autoplay,
    "Services.Autoplay"
);
